import type { RecorderController } from './RecorderController';
import type {
  PendingRecordingSegmentSettlement,
  RecordingSegmentServerApi,
  RecordingSegmentSettlementStore,
  RecordingSegmentType,
  RecordingServerSegment,
  RecordingSnapshotTrigger,
} from './types';
import {
  BrowserRecorderSettlementSignal,
  type RecorderSettlementSignal,
} from './recorderSettlementSignal';
import {
  BrowserRecorderSettlementLock,
  type RecorderSettlementLock,
} from './recorderSettlementLock';

export const MIN_SEGMENT_DURATION_MS = 500;
const DEFAULT_SETTLEMENT_REQUEST_TIMEOUT_MS = 10_000;
const DEFAULT_SETTLEMENT_RETRY_BASE_MS = 1_000;
const DEFAULT_SETTLEMENT_RETRY_MAX_MS = 30_000;
const MAX_BACKGROUND_SETTLEMENT_ATTEMPTS = 5;
const STOP_BOUNDARY_STATE_TIMEOUT_MS = 2_000;
const REMOTE_TERMINAL_INTENT_TTL_MS = 60_000;
const TERMINAL_INTENT_HEARTBEAT_MS = 30_000;

export type SegmentControlNotice = {
  id: number;
  tone: 'info' | 'success' | 'warning' | 'error';
  message: string;
};

export type RecordingSegmentView = RecordingServerSegment & {
  snapshotCount: number;
};

export type RecordingSegmentSnapshot = {
  phase: 'idle' | 'loading' | 'ready' | 'mutating' | 'error';
  recordingId: string | null;
  segments: RecordingSegmentView[];
  openSegment: RecordingSegmentView | null;
  pendingSettlements: number;
  terminalSettlements: number;
  cleanupSettlements: number;
  notice: SegmentControlNotice | null;
};

export type RecordingMomentRequest = {
  recordingId: string;
  segmentId: string;
  capturedAtMs: number;
  trigger: RecordingSnapshotTrigger;
  requestId: string;
};

/** Opaque, one-use capture policy result prepared for one user action. */
export type RecordingMomentPermit = {
  recordingId: string;
  token: string;
};

export type RecordingSegmentControllerDependencies = {
  server: RecordingSegmentServerApi;
  settlements: RecordingSegmentSettlementStore;
  /** TOADO-269 supplies the pixel capture. The controller already owns its idempotency key. */
  prepareMoment?: (recordingId: string) => Promise<RecordingMomentPermit>;
  releaseMoment?: (permit: RecordingMomentPermit | undefined) => void;
  captureMoment?: (
    request: RecordingMomentRequest,
    permit?: RecordingMomentPermit,
  ) => Promise<'captured' | 'blocked'>;
  now?: () => Date;
  settlementRequestTimeoutMs?: number;
  settlementRetryBaseMs?: number;
  settlementRetryMaxMs?: number;
  stopBoundaryStateTimeoutMs?: number;
  settlementSignal?: RecorderSettlementSignal;
  settlementLock?: RecorderSettlementLock;
  getAuthenticatedOwnerId?: () => string | null | undefined;
};

const INITIAL_SNAPSHOT: RecordingSegmentSnapshot = {
  phase: 'idle',
  recordingId: null,
  segments: [],
  openSegment: null,
  pendingSettlements: 0,
  terminalSettlements: 0,
  cleanupSettlements: 0,
  notice: null,
};

type SettlementOutcome =
  | { status: 'settled'; segment?: RecordingServerSegment }
  | { status: 'pending' }
  | { status: 'terminal' }
  | { status: 'failed' };

type SettlementOrigin = {
  ownerId: string;
  expiresAt?: string;
};

function safeTimelineMs(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(Number.MAX_SAFE_INTEGER, Math.floor(value)));
}

function segmentView(segment: RecordingServerSegment): RecordingSegmentView {
  return { ...segment, snapshotCount: segment.snapshots?.length ?? 0 };
}

/**
 * Shared ticket-boundary state machine for the in-tab, Document PiP, and
 * eventual extension controls. All callers observe one immutable snapshot.
 * User actions are serialized; the durable stop outbox may drain independently.
 */
export class RecordingSegmentController {
  private readonly listeners = new Set<() => void>();
  private snapshot: RecordingSegmentSnapshot = INITIAL_SNAPSHOT;
  private generation = 0;
  private noticeId = 0;
  private mutation: Promise<unknown> | null = null;
  private readonly reconciliations = new Map<string, Promise<void>>();
  private hydration: Promise<void> | null = null;
  private readonly recordedMomentKeys = new Set<string>();
  private readonly terminalSegmentKeys = new Set<string>();
  private readonly settlementRetryTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly settlementCleanupRetryTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly settlementTerminalRetryTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly terminalSettlementIntents = new Map<
    string,
    {
      recordingId: string;
      settlement?: PendingRecordingSegmentSettlement;
      releaseTerminalLock?: () => void;
    }
  >();
  private readonly remoteTerminalIntentExpiryTimers = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();
  private readonly terminalIntentHeartbeatTimers = new Map<
    string,
    ReturnType<typeof setInterval>
  >();
  private readonly settlementVersions = new Map<string, number>();
  private readonly settlementSignal: RecorderSettlementSignal;
  private readonly settlementLock: RecorderSettlementLock;
  private readonly ownsSettlementSignal: boolean;
  private unsubscribeRecorder: (() => void) | null = null;
  private unregisterStopParticipant: (() => void) | null = null;
  private unsubscribeSettlementSignal: (() => void) | null = null;
  private unsubscribeTerminalIntentSignal: (() => void) | null = null;
  private unsubscribeTerminalIntentRequests: (() => void) | null = null;

  constructor(
    private readonly recorder: RecorderController,
    private readonly dependencies: RecordingSegmentControllerDependencies,
  ) {
    this.settlementSignal = dependencies.settlementSignal ?? new BrowserRecorderSettlementSignal();
    this.settlementLock = dependencies.settlementLock ?? new BrowserRecorderSettlementLock();
    this.ownsSettlementSignal = !dependencies.settlementSignal;
  }

  connect(): () => void {
    if (!this.unsubscribeRecorder) this.unsubscribeRecorder = this.recorder.subscribe(this.observeRecorder);
    if (!this.unregisterStopParticipant) {
      this.unregisterStopParticipant = this.recorder.registerStopParticipant(this.persistStopBoundary);
    }
    if (!this.unsubscribeSettlementSignal) {
      this.unsubscribeSettlementSignal = this.settlementSignal.subscribe(this.onExternalSettlement);
    }
    if (!this.unsubscribeTerminalIntentSignal) {
      this.unsubscribeTerminalIntentSignal = this.settlementSignal
        .subscribeTerminalIntents(this.onExternalTerminalIntent);
    }
    if (!this.unsubscribeTerminalIntentRequests) {
      this.unsubscribeTerminalIntentRequests = this.settlementSignal
        .subscribeTerminalIntentRequests(this.onTerminalIntentRequest);
    }
    this.observeRecorder();
    return () => this.disconnect();
  }

  private disconnect(): void {
    this.unsubscribeRecorder?.();
    this.unsubscribeRecorder = null;
    this.unregisterStopParticipant?.();
    this.unregisterStopParticipant = null;
    this.unsubscribeSettlementSignal?.();
    this.unsubscribeSettlementSignal = null;
    this.unsubscribeTerminalIntentSignal?.();
    this.unsubscribeTerminalIntentSignal = null;
    this.unsubscribeTerminalIntentRequests?.();
    this.unsubscribeTerminalIntentRequests = null;
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): RecordingSegmentSnapshot => this.snapshot;

  dispose(): void {
    this.generation += 1;
    this.disconnect();
    this.settlementRetryTimers.forEach((timer) => clearTimeout(timer));
    this.settlementRetryTimers.clear();
    this.settlementCleanupRetryTimers.forEach((timer) => clearTimeout(timer));
    this.settlementCleanupRetryTimers.clear();
    this.settlementTerminalRetryTimers.forEach((timer) => clearTimeout(timer));
    this.settlementTerminalRetryTimers.clear();
    this.terminalSettlementIntents.forEach((intent) => intent.releaseTerminalLock?.());
    this.terminalSettlementIntents.clear();
    this.remoteTerminalIntentExpiryTimers.forEach((timer) => clearTimeout(timer));
    this.remoteTerminalIntentExpiryTimers.clear();
    this.terminalIntentHeartbeatTimers.forEach((timer) => clearInterval(timer));
    this.terminalIntentHeartbeatTimers.clear();
    this.settlementVersions.clear();
    this.terminalSegmentKeys.clear();
    if (this.ownsSettlementSignal) this.settlementSignal.dispose();
    this.listeners.clear();
  }

  dismissNotice(): void {
    if (this.snapshot.notice) this.publish({ notice: null });
  }

  private publish(patch: Partial<RecordingSegmentSnapshot>): void {
    const unchanged = (Object.keys(patch) as Array<keyof RecordingSegmentSnapshot>)
      .every((key) => Object.is(this.snapshot[key], patch[key]));
    if (unchanged) return;
    this.snapshot = { ...this.snapshot, ...patch };
    this.listeners.forEach((listener) => listener());
  }

  private notify(
    message: string,
    tone: SegmentControlNotice['tone'] = 'info',
    recordingId?: string,
  ): void {
    if (recordingId && this.snapshot.recordingId !== recordingId) return;
    this.publish({ notice: { id: ++this.noticeId, message, tone } });
  }

  private diagnostic(code: string): void {
    if (typeof window === 'undefined' || typeof CustomEvent === 'undefined') return;
    window.dispatchEvent(new CustomEvent('vd:recorder-diagnostic', {
      detail: { code, phase: this.snapshot.phase },
    }));
  }

  private onExternalSettlement = (recordingId: string): void => {
    this.bumpSettlementVersion(recordingId);
    this.scheduleExternalHydration(recordingId);
  };

  private onExternalTerminalIntent = (
    recordingId: string,
    settlementKey: string,
    active: boolean,
  ): void => {
    if (active) {
      this.installRemoteTerminalIntent(recordingId, settlementKey);
      return;
    }
    const timer = this.remoteTerminalIntentExpiryTimers.get(settlementKey);
    if (timer) clearTimeout(timer);
    this.remoteTerminalIntentExpiryTimers.delete(settlementKey);
    this.terminalSettlementIntents.delete(settlementKey);
  };

  private onTerminalIntentRequest = (): void => {
    this.terminalSettlementIntents.forEach((intent, settlementKey) => {
      if (intent.settlement && intent.releaseTerminalLock) {
        this.settlementSignal.publishTerminalIntent(intent.recordingId, settlementKey, true);
      }
    });
  };

  private installRemoteTerminalIntent(recordingId: string, settlementKey: string): void {
    const existingIntent = this.terminalSettlementIntents.get(settlementKey);
    if (existingIntent?.settlement) return;
    this.terminalSettlementIntents.set(settlementKey, { recordingId });
    const existingTimer = this.remoteTerminalIntentExpiryTimers.get(settlementKey);
    if (existingTimer) clearTimeout(existingTimer);
    const timer = setTimeout(() => {
      this.remoteTerminalIntentExpiryTimers.delete(settlementKey);
      const intent = this.terminalSettlementIntents.get(settlementKey);
      if (intent && !intent.settlement) {
        this.terminalSettlementIntents.delete(settlementKey);
        void this.recoverExpiredRemoteIntent(recordingId, settlementKey);
      }
    }, REMOTE_TERMINAL_INTENT_TTL_MS);
    this.remoteTerminalIntentExpiryTimers.set(settlementKey, timer);
  }

  private async recoverExpiredRemoteIntent(recordingId: string, settlementKey: string): Promise<void> {
    try {
      const settlement = (await this.dependencies.settlements
        .listSegmentSettlementsForRecording(recordingId))
        .find((candidate) => candidate.key === settlementKey && candidate.state === 'pending');
      if (!settlement) return;
      if (this.dependencies.getAuthenticatedOwnerId?.() !== settlement.ownerId) return;
      await this.reconcilePending(settlement.ownerId);
    } catch {
      this.diagnostic('segment_terminal_intent_expiry_recovery_failed');
    }
  }

  private bumpSettlementVersion(recordingId: string): void {
    this.settlementVersions.set(recordingId, (this.settlementVersions.get(recordingId) ?? 0) + 1);
  }

  private markSettlementMetadataChanged(recordingId: string): void {
    this.bumpSettlementVersion(recordingId);
    this.settlementSignal.publish(recordingId);
  }

  private scheduleExternalHydration(recordingId: string): void {
    if (this.snapshot.recordingId !== recordingId) return;
    const mutation = this.mutation;
    if (mutation) {
      const resume = () => this.scheduleExternalHydration(recordingId);
      void mutation.then(resume, resume);
      return;
    }
    // An active hydration observes the increment inside its stable-read loop.
    if (this.hydration) return;
    void this.startHydration(recordingId);
  }

  private observeRecorder = (): void => {
    const recorderSnapshot = this.recorder.getSnapshot();
    const recordingId = recorderSnapshot.session?.id ?? null;
    if (recorderSnapshot.phase === 'recording' && recordingId) {
      if (recordingId !== this.snapshot.recordingId) void this.startHydration(recordingId);
      return;
    }
    if (recorderSnapshot.phase === 'stopping' && recordingId) {
      if (recordingId !== this.snapshot.recordingId) {
        void this.startHydration(recordingId);
      }
      return;
    }
    if (!recordingId && recorderSnapshot.phase === 'idle' && this.snapshot.recordingId) {
      this.generation += 1;
      // Detach work from the completed lifecycle. A late server response is
      // still allowed to run its cleanup, but it cannot block the next session.
      this.mutation = null;
      this.recordedMomentKeys.clear();
      this.terminalSegmentKeys.clear();
      this.publish(INITIAL_SNAPSHOT);
    }
  };

  private startHydration(recordingId: string): Promise<void> {
    if (this.hydration && this.snapshot.recordingId === recordingId) return this.hydration;
    const operation = this.hydrate(recordingId);
    this.hydration = operation;
    void operation.finally(() => {
      if (this.hydration === operation) this.hydration = null;
    });
    return operation;
  }

  private async hydrate(recordingId: string): Promise<void> {
    const generation = ++this.generation;
    this.recordedMomentKeys.clear();
    this.publish({
      phase: 'loading',
      recordingId,
      segments: [],
      openSegment: null,
      pendingSettlements: 0,
      terminalSettlements: 0,
      cleanupSettlements: 0,
      notice: null,
    });
    try {
      let session: Awaited<ReturnType<RecordingSegmentServerApi['get']>> | null = null;
      let settlementRows: PendingRecordingSegmentSettlement[] = [];
      for (let attempt = 0; attempt < 5; attempt += 1) {
        const version = this.settlementVersions.get(recordingId) ?? 0;
        session = await this.dependencies.server.get(recordingId);
        settlementRows = await this.dependencies.settlements
          .listSegmentSettlementsForRecording(recordingId);
        if ((this.settlementVersions.get(recordingId) ?? 0) === version) break;
        session = null;
      }
      if (!session) throw new Error('Ticket boundary state changed repeatedly during hydration.');
      if (generation !== this.generation || session.id !== recordingId) return;
      const serverSegments = session.segments ?? [];
      serverSegments.forEach((segment) => {
        if (segment.endMs !== undefined || segment.droppedAt) {
          this.terminalSegmentKeys.add(`${segment.sessionId}:${segment.id}`);
        }
      });
      const segments = serverSegments
        .filter((segment) => !segment.droppedAt)
        .filter((segment) => segment.endMs !== undefined
          || !this.terminalSegmentKeys.has(`${segment.sessionId}:${segment.id}`))
        .map(segmentView);
      const openSegment = segments.find((segment) => segment.endMs === undefined) ?? null;
      const pendingSettlements = settlementRows.length;
      const terminalSettlements = settlementRows.filter((row) => row.state === 'terminal').length;
      const cleanupSettlements = settlementRows.filter((row) => row.state === 'cleanup').length;
      const retryableSettlements = settlementRows.filter((row) => row.state === 'pending').length;
      if (generation !== this.generation) return;
      this.publish({
        phase: 'ready',
        recordingId,
        segments,
        openSegment,
        pendingSettlements,
        terminalSettlements,
        cleanupSettlements,
      });
      if (pendingSettlements > 0) {
        this.notify(
          terminalSettlements > 0
            ? 'A saved ticket boundary needs review before it can sync.'
            : cleanupSettlements > 0
              ? 'A ticket boundary is saved. Local cleanup will retry.'
              : 'A ticket boundary is saved locally for retry.',
          terminalSettlements > 0 ? 'error' : 'warning',
        );
        if (retryableSettlements > 0) {
          void this.reconcileRecording(recordingId).catch(() => {
            this.diagnostic('segment_settlement_recovery_failed');
          });
        }
      }
    } catch {
      if (generation !== this.generation) return;
      this.diagnostic('segment_hydration_failed');
      this.publish({ phase: 'error' });
      this.notify('Ticket controls could not load. Your recording is still safe.', 'error');
    }
  }

  private recorderIsActiveFor(recordingId: string): boolean {
    const current = this.recorder.getSnapshot();
    return current.session?.id === recordingId
      && current.phase === 'recording';
  }

  private async runMutation<T>(operation: () => Promise<T>): Promise<T | undefined> {
    if (this.mutation) {
      this.notify('The previous ticket action is still finishing.', 'warning');
      return undefined;
    }
    this.publish({ phase: 'mutating' });
    const mutation = operation();
    this.mutation = mutation;
    try {
      return await mutation;
    } finally {
      if (this.mutation === mutation) {
        this.mutation = null;
        if (this.snapshot.phase === 'mutating') this.publish({ phase: 'ready' });
      }
    }
  }

  private replaceSegment(next: RecordingSegmentView): void {
    if (this.snapshot.recordingId !== next.sessionId) return;
    const segmentKey = `${next.sessionId}:${next.id}`;
    const nextIsOpen = next.endMs === undefined && !next.droppedAt;
    const current = this.snapshot.segments.find((segment) => segment.id === next.id);
    if (nextIsOpen && this.terminalSegmentKeys.has(segmentKey) && !current) return;
    if (!nextIsOpen) this.terminalSegmentKeys.add(segmentKey);
    const currentIsTerminal = current
      && (current.endMs !== undefined || Boolean(current.droppedAt));
    const replacement = current && currentIsTerminal && nextIsOpen
      ? {
          ...next,
          ...(current.endMs === undefined ? {} : { endMs: current.endMs }),
          ...(current.droppedAt ? { droppedAt: current.droppedAt } : {}),
          snapshotCount: Math.max(current.snapshotCount, next.snapshotCount),
        }
      : {
          ...next,
          snapshotCount: Math.max(current?.snapshotCount ?? 0, next.snapshotCount),
        };
    const segments = this.snapshot.segments.some((segment) => segment.id === next.id)
      ? this.snapshot.segments.map((segment) => segment.id === next.id ? replacement : segment)
      : [...this.snapshot.segments, replacement];
    this.publish({
      segments,
      openSegment: replacement.endMs === undefined && !replacement.droppedAt ? replacement : null,
    });
  }

  private async recordMoment(
    segment: RecordingSegmentView,
    trigger: RecordingSnapshotTrigger,
    capturedAtMs: number,
    permit?: RecordingMomentPermit,
  ): Promise<RecordingSegmentView> {
    if (!this.dependencies.captureMoment) return segment;
    const suffix = trigger === 'manual' ? String(segment.snapshotCount) : trigger;
    const requestId = `${segment.id}:${suffix}`;
    if (this.recordedMomentKeys.has(requestId)) return segment;
    const request = {
      recordingId: segment.sessionId,
      segmentId: segment.id,
      capturedAtMs,
      trigger,
      requestId,
    };
    const outcome = permit
      ? await this.dependencies.captureMoment(request, permit)
      : await this.dependencies.captureMoment(request);
    if (outcome === 'blocked') return segment;
    this.recordedMomentKeys.add(requestId);
    return { ...segment, snapshotCount: segment.snapshotCount + 1 };
  }

  async startSegment(segmentType: RecordingSegmentType): Promise<void> {
    const recorderSnapshot = this.recorder.getSnapshot();
    const localSession = recorderSnapshot.session;
    const recordingId = localSession?.id;
    if (recorderSnapshot.phase !== 'recording' || !recordingId) {
      this.notify('Start the walkthrough before describing a ticket.', 'warning');
      return;
    }
    const settlementOrigin: SettlementOrigin = {
      ownerId: localSession.ownerId,
      ...(localSession.expiresAt ? { expiresAt: localSession.expiresAt } : {}),
    };
    if (this.snapshot.phase === 'loading' || this.snapshot.recordingId !== recordingId) {
      this.notify('Ticket controls are still loading.', 'warning');
      return;
    }
    if (this.snapshot.openSegment) {
      this.notify('Finish the current ticket before starting another.', 'warning');
      return;
    }
    let permit: RecordingMomentPermit | undefined;
    try {
      permit = await this.dependencies.prepareMoment?.(recordingId);
    } catch {
      this.notify('Capture policy could not be verified. Try starting the ticket again.', 'error', recordingId);
      return;
    }
    const startMs = safeTimelineMs(this.recorder.getTimelineMs());
    try {
      await this.runMutation(async () => {
      let opened: RecordingSegmentView | null = null;
      try {
        opened = segmentView(await this.dependencies.server.open(recordingId, { startMs, segmentType }));
        this.replaceSegment(opened);
        if (!this.recorderIsActiveFor(recordingId)) {
          // The authenticated user may have changed while the open request was
          // in flight. Persist the original ownership context, then let the
          // same-owner recovery worker send the cancellation after sign-in.
          const outcome = await this.queueLateCancellation(opened, settlementOrigin);
          if (outcome.status !== 'settled') {
            this.notify(
              outcome.status === 'pending'
                ? 'The late ticket was saved for automatic cleanup.'
                : 'The late ticket could not be queued for cleanup.',
              outcome.status === 'pending' ? 'warning' : 'error',
              recordingId,
            );
          }
          return;
        }
        this.notify(
          segmentType === 'bug' ? 'Bug description started.' : 'Feature request started.',
          'success',
          recordingId,
        );
        try {
          const withMoment = await this.recordMoment(opened, 'start', startMs, permit);
          this.replaceSegment(withMoment);
        } catch {
          this.diagnostic('segment_start_snapshot_failed');
          this.notify('The ticket started, but its opening snapshot needs a retry.', 'warning', recordingId);
        }
      } catch {
        this.diagnostic('segment_start_failed');
        this.notify('The ticket could not start. Keep recording and try again.', 'error', recordingId);
      }
      });
    } finally {
      this.dependencies.releaseMoment?.(permit);
    }
  }

  async captureMoment(): Promise<void> {
    const open = this.snapshot.openSegment;
    if (!open) {
      this.notify('Start a ticket before capturing a moment.', 'warning');
      return;
    }
    if (!this.dependencies.captureMoment) {
      this.notify('Moment capture is not available in this build yet.', 'warning');
      return;
    }
    let permit: RecordingMomentPermit | undefined;
    try {
      permit = await this.dependencies.prepareMoment?.(open.sessionId);
    } catch {
      this.notify('Capture policy could not be verified. Try the moment again.', 'error', open.sessionId);
      return;
    }
    const capturedAtMs = safeTimelineMs(this.recorder.getTimelineMs());
    try {
      await this.runMutation(async () => {
      try {
        const updated = await this.recordMoment(open, 'manual', capturedAtMs, permit);
        this.replaceSegment(updated);
        this.notify(
          updated.snapshotCount === open.snapshotCount
            ? 'Moment blocked by the workspace capture policy.'
            : `Moment ${updated.snapshotCount} captured.`,
          updated.snapshotCount === open.snapshotCount ? 'warning' : 'success',
          open.sessionId,
        );
      } catch {
        this.diagnostic('segment_capture_failed');
        this.notify('That moment was not captured. Try it again.', 'error', open.sessionId);
      }
      });
    } finally {
      this.dependencies.releaseMoment?.(permit);
    }
  }

  private remainingDuration(open: RecordingSegmentView, endMs: number): number {
    return Math.max(0, MIN_SEGMENT_DURATION_MS - (endMs - open.startMs));
  }

  async finishSegment(): Promise<boolean> {
    const open = this.snapshot.openSegment;
    if (!open) {
      this.notify('There is no open ticket to finish.', 'warning');
      return false;
    }
    let permit: RecordingMomentPermit | undefined;
    try {
      permit = await this.dependencies.prepareMoment?.(open.sessionId);
    } catch {
      this.notify('Capture policy could not be verified. Try finishing the ticket again.', 'error', open.sessionId);
      return false;
    }
    const endMs = safeTimelineMs(this.recorder.getTimelineMs());
    const remaining = this.remainingDuration(open, endMs);
    if (remaining > 0) {
      this.dependencies.releaseMoment?.(permit);
      this.notify(`Keep describing for ${Math.ceil(remaining / 100) / 10} more seconds.`, 'warning');
      return false;
    }
    try {
      const result = await this.runMutation(() => this.closeOpenSegment(open, endMs, false, permit));
      return result?.status === 'settled';
    } finally {
      this.dependencies.releaseMoment?.(permit);
    }
  }

  private async closeOpenSegment(
    open: RecordingSegmentView,
    endMs: number,
    forced: boolean,
    permit?: RecordingMomentPermit,
    captureFinish = true,
  ): Promise<SettlementOutcome> {
    let withFinishMoment = open;
    if (this.dependencies.captureMoment && captureFinish) {
      try {
        withFinishMoment = await this.recordMoment(open, 'finish', endMs, permit);
      } catch {
        this.diagnostic('segment_finish_snapshot_failed');
        this.notify(
          'The finish snapshot failed, but the ticket boundary is still being saved.',
          'warning',
          open.sessionId,
        );
      }
    }
    this.replaceSegment(withFinishMoment);
    const outcome = await this.settleSegment(open, 'close', endMs);
    if (outcome.status !== 'settled' || !outcome.segment) {
      if (outcome.status === 'pending') {
        this.notify('The ticket boundary is saved locally for retry.', 'warning', open.sessionId);
      } else if (outcome.status === 'terminal') {
        this.notify('The saved ticket boundary needs review before it can sync.', 'error', open.sessionId);
      } else {
        this.notify(
          'The ticket boundary could not be saved locally. Keep this tab open.',
          'error',
          open.sessionId,
        );
      }
      return outcome;
    }
    const closed = segmentView(outcome.segment);
    const completed = { ...closed, snapshotCount: withFinishMoment.snapshotCount };
    this.replaceSegment(completed);
    this.notify(
      forced ? 'The open ticket was saved when recording ended.' : 'Ticket description finished.',
      'success',
      open.sessionId,
    );
    return outcome;
  }

  async stopRecording(): Promise<void> {
    if (this.recorder.getSnapshot().phase !== 'recording') return;
    // Stop always ends capture, whatever becomes of the ticket boundary, and
    // soon: it kept screen and microphone recording when the boundary could
    // not be saved, once its notice had faded (review of 0.2.0, 3 Oct), and
    // would have again had saving it thrown, or never answered. A save still
    // out when capture ends goes on, and says how it went.
    let timer: ReturnType<typeof setTimeout> | undefined;
    const settled = await Promise.race([
      this.settleBoundaryForStop().then(() => true, () => false),
      new Promise<false>((resolve) => {
        timer = setTimeout(
          () => resolve(false),
          this.dependencies.stopBoundaryStateTimeoutMs ?? STOP_BOUNDARY_STATE_TIMEOUT_MS,
        );
      }),
    ]);
    clearTimeout(timer);
    if (!settled) this.notify('Stopping. This ticket ends where the recording does.', 'warning');
    await this.recorder.stop('user');
  }

  /**
   * The open ticket's boundary, as Stop ends capture. Processing ends the open
   * ticket where the media stops, which is here, so nothing is lost when the
   * boundary cannot be saved. A ticket too short to keep whose cancel was not
   * saved can come back from processing as a moment of its own, which review
   * can drop.
   */
  private async settleBoundaryForStop(): Promise<void> {
    if (this.mutation) {
      this.notify('Stopping now. The current ticket action will finish or be recovered.', 'warning');
      return;
    }
    const open = this.snapshot.openSegment;
    if (!open) return;
    const settlementKey = `${open.sessionId}:${open.id}`;
    const existing = (await this.dependencies.settlements
      .listSegmentSettlementsForRecording(open.sessionId))
      .find((candidate) => candidate.key === settlementKey);
    if (existing?.state === 'terminal' || this.terminalSettlementIntents.has(settlementKey)) {
      this.notify('Stopping with a ticket boundary that still needs review.', 'error');
      return;
    }
    if (existing?.state === 'cleanup') {
      this.notify('The ticket is saved. Local cleanup will continue after Stop.', 'warning');
      return;
    }
    // Stop must not wait on a policy request while Chrome is still sharing
    // screen and microphone. The explicit Finish action captures a closing
    // image; Stop prioritizes ending capture and persists the boundary only,
    // so it takes no moment permit.
    const boundary = safeTimelineMs(this.recorder.getTimelineMs());
    const closing = this.remainingDuration(open, boundary) === 0;
    const outcome = await this.runMutation(() => closing
      ? this.closeOpenSegment(open, boundary, true, undefined, false)
      : this.cancelOpenSegment(open));
    if (outcome?.status === 'failed') {
      this.notify(closing
        ? 'Stopping. This ticket ends where the recording does.'
        : 'Stopping. If a very short moment appears in review, drop it there.', 'warning', open.sessionId);
    }
  }

  /** Finish boundary work while authentication is still available. */
  async prepareForSignOut(): Promise<void> {
    const recorderSnapshot = this.recorder.getSnapshot();
    if (recorderSnapshot.phase === 'recording') {
      const boundary = safeTimelineMs(this.recorder.getTimelineMs());
      await this.persistStopBoundary(boundary).catch(() => {
        this.diagnostic('segment_signout_boundary_failed');
        this.notify('The recording will stop, but its open ticket boundary needs review.', 'error');
      });
    }
    await this.recorder.prepareForSignOut();
  }

  private async cancelOpenSegment(open: RecordingSegmentView): Promise<SettlementOutcome> {
    return this.settleSegment(open, 'cancel');
  }

  private async queueLateCancellation(
    open: RecordingSegmentView,
    origin: SettlementOrigin,
  ): Promise<SettlementOutcome> {
    try {
      await this.ensureSettlement(open, 'cancel', undefined, origin);
      this.diagnostic('segment_late_cancel_queued');
      if (this.dependencies.getAuthenticatedOwnerId?.() === origin.ownerId) {
        void this.reconcilePending(origin.ownerId);
      }
      return { status: 'pending' };
    } catch {
      this.diagnostic('segment_settlement_persist_failed');
      return { status: 'failed' };
    }
  }

  /** Retry persisted idempotent close/cancel intents while auth is available. */
  async reconcilePending(ownerId: string): Promise<void> {
    try {
      await this.settlementSignal.requestTerminalIntents();
      await this.retryTerminalMetadataForOwner(ownerId);
      await this.dependencies.settlements.pruneExpiredSegmentSettlements(this.now());
      const settlements = await this.dependencies.settlements.listSegmentSettlementsForOwner(ownerId);
      const cleanupRecovery = this.recoverCleanupSettlements(
        settlements.filter((settlement) => settlement.state === 'cleanup'),
      );
      const recorderSnapshot = this.recorder.getSnapshot();
      const currentRecordingId = ['recording', 'stopping'].includes(recorderSnapshot.phase)
        ? recorderSnapshot.session?.id
        : undefined;
      const byRecording = new Map<string, PendingRecordingSegmentSettlement[]>();
      settlements
        .filter((settlement) => settlement.state === 'pending')
        .forEach((settlement) => {
          const group = byRecording.get(settlement.recordingId) ?? [];
          group.push(settlement);
          byRecording.set(settlement.recordingId, group);
        });
      const groups = [...byRecording.entries()].sort(([left], [right]) => {
        if (left === currentRecordingId) return -1;
        if (right === currentRecordingId) return 1;
        return 0;
      });
      let cursor = 0;
      const worker = async () => {
        while (cursor < groups.length) {
          const [recordingId, rows] = groups[cursor++]!;
          await this.enqueueRecordingReconciliation(
            recordingId,
            () => this.replaySettlements(rows),
          );
        }
      };
      await Promise.all([
        cleanupRecovery,
        ...Array.from({ length: Math.min(3, groups.length) }, () => worker()),
      ]);
    } catch {
      this.diagnostic('segment_settlement_recovery_failed');
    }
  }

  private reconcileRecording(recordingId: string): Promise<void> {
    return this.enqueueRecordingReconciliation(recordingId, async () => {
      const settlements = await this.dependencies.settlements
        .listSegmentSettlementsForRecording(recordingId);
      await this.replaySettlements(settlements.filter((settlement) => settlement.state === 'pending'));
    });
  }

  private enqueueRecordingReconciliation(
    recordingId: string,
    operation: () => Promise<void>,
  ): Promise<void> {
    const previous = this.reconciliations.get(recordingId) ?? Promise.resolve();
    const queued = previous.then(operation, operation);
    const tracked = queued.catch(() => {}).finally(() => {
      if (this.reconciliations.get(recordingId) === tracked) {
        this.reconciliations.delete(recordingId);
      }
    });
    this.reconciliations.set(recordingId, tracked);
    return queued;
  }

  private async replaySettlements(settlements: PendingRecordingSegmentSettlement[]): Promise<void> {
    for (const settlement of settlements) {
      if (this.terminalSettlementIntents.has(settlement.key)) continue;
      const current = (await this.dependencies.settlements
        .listSegmentSettlementsForRecording(settlement.recordingId))
        .find((candidate) => candidate.key === settlement.key);
      if (current?.state !== 'pending') continue;
      const retry: PendingRecordingSegmentSettlement = {
        ...current,
        attempts: current.attempts + 1,
        lastAttemptAt: this.now().toISOString(),
      };
      try {
        const claimed = await this.dependencies.settlements
          .putSegmentSettlementIfState(retry, 'pending');
        if (!claimed) continue;
        const outcome = await this.sendDurableSettlement(retry);
        if (outcome.status === 'settled') {
          this.diagnostic('segment_settlement_recovered');
        }
      } catch {
        this.diagnostic('segment_settlement_recovery_failed');
      }
    }
  }

  private async recoverCleanupSettlements(
    settlements: PendingRecordingSegmentSettlement[],
  ): Promise<void> {
    let cursor = 0;
    const worker = async () => {
      while (cursor < settlements.length) {
        const settlement = settlements[cursor++]!;
        const current = (await this.dependencies.settlements
          .listSegmentSettlementsForRecording(settlement.recordingId))
          .find((candidate) => candidate.key === settlement.key);
        if (current?.state !== 'cleanup') continue;
        try {
          await this.dependencies.settlements.deleteSegmentSettlement(current.key);
          const timer = this.settlementCleanupRetryTimers.get(current.key);
          if (timer) clearTimeout(timer);
          this.settlementCleanupRetryTimers.delete(current.key);
          this.markSettlementMetadataChanged(current.recordingId);
          await this.refreshPendingSettlementCount().catch(() => {});
          this.diagnostic('segment_settlement_cleanup_recovered');
        } catch {
          this.scheduleSettlementCleanup(current);
        }
      }
    };
    await Promise.all(Array.from(
      { length: Math.min(3, settlements.length) },
      () => worker(),
    ));
  }

  private async settleSegment(
    segment: RecordingSegmentView,
    action: PendingRecordingSegmentSettlement['action'],
    endMs?: number,
    origin?: SettlementOrigin,
  ): Promise<SettlementOutcome> {
    try {
      const settlement = await this.ensureSettlement(segment, action, endMs, origin);
      return this.sendDurableSettlement(settlement);
    } catch {
      this.diagnostic('segment_settlement_persist_failed');
      return { status: 'failed' };
    }
  }

  private async ensureSettlement(
    segment: RecordingSegmentView,
    action: PendingRecordingSegmentSettlement['action'],
    endMs?: number,
    origin?: SettlementOrigin,
  ): Promise<PendingRecordingSegmentSettlement> {
    const timestamp = this.now().toISOString();
    const key = `${segment.sessionId}:${segment.id}`;
    const existingSettlements = await this.dependencies.settlements
      .listSegmentSettlementsForRecording(segment.sessionId);
    const existing = existingSettlements.find((candidate) => candidate.key === key);
    if (existing) {
      if (existing.action !== action) {
        this.diagnostic('segment_settlement_conflict');
        throw new Error('A conflicting settlement is already durable.');
      }
      if (existing.state !== 'pending') {
        this.diagnostic('segment_settlement_state_conflict');
        throw new Error('The durable settlement is no longer retryable.');
      }
      if (this.terminalSettlementIntents.has(existing.key)) {
        throw new Error('The terminal settlement state is still being saved locally.');
      }
      const retry = {
        ...existing,
        attempts: existing.attempts + 1,
        lastAttemptAt: timestamp,
      };
      const claimed = await this.dependencies.settlements
        .putSegmentSettlementIfState(retry, 'pending');
      if (!claimed) {
        this.diagnostic('segment_settlement_state_conflict');
        throw new Error('The durable settlement changed in another tab.');
      }
      await this.refreshPendingSettlementCount();
      return retry;
    }
    const activeSession = this.recorder.getSnapshot().session;
    const ownerId = origin?.ownerId ?? activeSession?.ownerId ?? '';
    const expiresAt = origin?.expiresAt ?? activeSession?.expiresAt;
    const settlement: PendingRecordingSegmentSettlement = {
      key,
      ownerId,
      recordingId: segment.sessionId,
      segmentId: segment.id,
      action,
      state: 'pending',
      ...(action === 'close' ? { endMs: safeTimelineMs(endMs ?? 0) } : {}),
      createdAt: timestamp,
      expiresAt: expiresAt ?? new Date(this.now().getTime() + 7 * 24 * 60 * 60 * 1_000).toISOString(),
      lastAttemptAt: timestamp,
      attempts: 1,
    };
    if (!settlement.ownerId) {
      this.diagnostic('segment_settlement_owner_missing');
      throw new Error('The settlement owner is unavailable.');
    }
    const created = await this.dependencies.settlements
      .putSegmentSettlementIfState(settlement, null);
    if (!created) {
      const concurrent = (await this.dependencies.settlements
        .listSegmentSettlementsForRecording(segment.sessionId))
        .find((candidate) => candidate.key === key);
      if (concurrent?.state === 'pending' && concurrent.action === action) {
        await this.refreshPendingSettlementCount();
        return concurrent;
      }
      this.diagnostic('segment_settlement_create_conflict');
      throw new Error('Another tab changed the durable settlement first.');
    }
    this.markSettlementMetadataChanged(settlement.recordingId);
    await this.refreshPendingSettlementCount();
    return settlement;
  }

  private async sendDurableSettlement(
    settlement: PendingRecordingSegmentSettlement,
  ): Promise<SettlementOutcome> {
    return this.settlementLock.runExclusive(settlement.key, async () => {
      if (this.terminalSettlementIntents.has(settlement.key)) return { status: 'terminal' };
      if (await this.settlementLock.isTerminalIntentActive(settlement.key)) {
        this.installRemoteTerminalIntent(settlement.recordingId, settlement.key);
        return { status: 'terminal' };
      }
      let current: PendingRecordingSegmentSettlement | undefined;
      try {
        current = (await this.dependencies.settlements
          .listSegmentSettlementsForRecording(settlement.recordingId))
          .find((candidate) => candidate.key === settlement.key);
      } catch {
        this.diagnostic('segment_settlement_lock_state_failed');
        return { status: 'failed' };
      }
      if (!current || current.state === 'cleanup') {
        if (settlement.action === 'cancel') return { status: 'settled' };
        try {
          const session = await this.dependencies.server.get(settlement.recordingId);
          const authoritative = session.segments?.find(
            (candidate) => candidate.id === settlement.segmentId && candidate.endMs !== undefined,
          );
          if (authoritative) return { status: 'settled', segment: authoritative };
        } catch {
          this.diagnostic('segment_settlement_authoritative_read_failed');
        }
        return { status: 'failed' };
      }
      if (current.state === 'terminal') return { status: 'terminal' };
      return this.sendDurableSettlementLocked(current);
    });
  }

  private async sendDurableSettlementLocked(
    settlement: PendingRecordingSegmentSettlement,
  ): Promise<SettlementOutcome> {
    const abortController = new AbortController();
    const timeout = setTimeout(
      () => abortController.abort(new DOMException('Settlement request timed out.', 'TimeoutError')),
      this.dependencies.settlementRequestTimeoutMs ?? DEFAULT_SETTLEMENT_REQUEST_TIMEOUT_MS,
    );
    let segment: RecordingServerSegment | undefined;
    try {
      segment = settlement.action === 'close'
        ? await this.dependencies.server.close(
          settlement.recordingId,
          settlement.segmentId,
          settlement.endMs!,
          abortController.signal,
        )
        : undefined;
      if (settlement.action === 'cancel') {
        await this.dependencies.server.cancel(
          settlement.recordingId,
          settlement.segmentId,
          abortController.signal,
        );
      }
    } catch (error) {
      clearTimeout(timeout);
      if (this.isTerminalSettlementError(error)) {
        const terminal = {
          ...settlement,
          state: 'terminal' as const,
          lastErrorCode: this.settlementErrorCode(error),
          lastAttemptAt: this.now().toISOString(),
        };
        await this.installTerminalIntent(terminal);
        const persisted = await this.persistTerminalSettlement(terminal);
        if (!persisted) {
          this.diagnostic('segment_settlement_terminal_state_pending');
          return { status: 'failed' };
        }
        this.diagnostic('segment_settlement_terminal');
        return { status: 'terminal' };
      }
      this.scheduleSettlementRetry(settlement, error);
      this.diagnostic('segment_settlement_pending');
      return { status: 'pending' };
    }
    clearTimeout(timeout);
    const settled: SettlementOutcome = { status: 'settled', ...(segment ? { segment } : {}) };
    this.bumpSettlementVersion(settlement.recordingId);
    this.applySettlementOutcome(settlement, settled);
    const retryTimer = this.settlementRetryTimers.get(settlement.key);
    if (retryTimer) clearTimeout(retryTimer);
    this.settlementRetryTimers.delete(settlement.key);
    try {
      await this.dependencies.settlements.deleteSegmentSettlement(settlement.key);
    } catch {
      const cleanup = {
        ...settlement,
        state: 'cleanup' as const,
        lastErrorCode: 'local_cleanup_pending',
        lastAttemptAt: this.now().toISOString(),
      };
      const cleanupPersisted = await this.dependencies.settlements
        .putSegmentSettlementIfState(cleanup, 'pending')
        .catch(() => false);
      if (cleanupPersisted) {
        this.markSettlementMetadataChanged(settlement.recordingId);
      } else {
        this.diagnostic('segment_settlement_cleanup_state_failed');
      }
      this.scheduleSettlementCleanup(cleanup);
      this.diagnostic('segment_settlement_cleanup_pending');
      await this.refreshPendingSettlementCount().catch(() => {});
      return settled;
    }
    const cleanupTimer = this.settlementCleanupRetryTimers.get(settlement.key);
    if (cleanupTimer) clearTimeout(cleanupTimer);
    this.settlementCleanupRetryTimers.delete(settlement.key);
    this.markSettlementMetadataChanged(settlement.recordingId);
    await this.refreshPendingSettlementCount().catch(() => {
      this.diagnostic('segment_settlement_count_failed');
    });
    return settled;
  }

  private scheduleSettlementRetry(
    settlement: PendingRecordingSegmentSettlement,
    error: unknown,
  ): void {
    const status = this.errorStatus(error);
    if (
      settlement.attempts >= MAX_BACKGROUND_SETTLEMENT_ATTEMPTS
      || status === 401
      || status === 404
      || this.settlementRetryTimers.has(settlement.key)
    ) return;
    const base = this.dependencies.settlementRetryBaseMs ?? DEFAULT_SETTLEMENT_RETRY_BASE_MS;
    const maximum = this.dependencies.settlementRetryMaxMs ?? DEFAULT_SETTLEMENT_RETRY_MAX_MS;
    const delay = Math.min(maximum, base * (2 ** Math.max(0, settlement.attempts - 1)));
    const timer = setTimeout(() => {
      this.settlementRetryTimers.delete(settlement.key);
      void this.retrySettlement(settlement);
    }, delay);
    this.settlementRetryTimers.set(settlement.key, timer);
  }

  private scheduleSettlementCleanup(
    settlement: PendingRecordingSegmentSettlement,
    attempt = 1,
  ): void {
    if (
      attempt > MAX_BACKGROUND_SETTLEMENT_ATTEMPTS
      || this.settlementCleanupRetryTimers.has(settlement.key)
    ) return;
    const base = this.dependencies.settlementRetryBaseMs ?? DEFAULT_SETTLEMENT_RETRY_BASE_MS;
    const maximum = this.dependencies.settlementRetryMaxMs ?? DEFAULT_SETTLEMENT_RETRY_MAX_MS;
    const delay = Math.min(maximum, base * (2 ** Math.max(0, attempt - 1)));
    const timer = setTimeout(() => {
      this.settlementCleanupRetryTimers.delete(settlement.key);
      void this.dependencies.settlements.deleteSegmentSettlement(settlement.key).then(async () => {
        this.markSettlementMetadataChanged(settlement.recordingId);
        await this.refreshPendingSettlementCount().catch(() => {});
        this.diagnostic('segment_settlement_cleanup_recovered');
      }).catch(() => {
        this.scheduleSettlementCleanup(settlement, attempt + 1);
      });
    }, delay);
    this.settlementCleanupRetryTimers.set(settlement.key, timer);
  }

  private async installTerminalIntent(
    settlement: PendingRecordingSegmentSettlement,
  ): Promise<void> {
    const remoteTimer = this.remoteTerminalIntentExpiryTimers.get(settlement.key);
    if (remoteTimer) clearTimeout(remoteTimer);
    this.remoteTerminalIntentExpiryTimers.delete(settlement.key);
    const releaseTerminalLock = await this.settlementLock.holdTerminalIntent(settlement.key);
    this.terminalSettlementIntents.set(settlement.key, {
      recordingId: settlement.recordingId,
      settlement,
      releaseTerminalLock,
    });
    this.settlementSignal.publishTerminalIntent(
      settlement.recordingId,
      settlement.key,
      true,
    );
    if (!this.terminalIntentHeartbeatTimers.has(settlement.key)) {
      const heartbeat = setInterval(() => {
        this.settlementSignal.publishTerminalIntent(
          settlement.recordingId,
          settlement.key,
          true,
        );
      }, TERMINAL_INTENT_HEARTBEAT_MS);
      this.terminalIntentHeartbeatTimers.set(settlement.key, heartbeat);
    }
  }

  private clearTerminalIntent(settlement: PendingRecordingSegmentSettlement): void {
    const remoteTimer = this.remoteTerminalIntentExpiryTimers.get(settlement.key);
    if (remoteTimer) clearTimeout(remoteTimer);
    this.remoteTerminalIntentExpiryTimers.delete(settlement.key);
    const heartbeat = this.terminalIntentHeartbeatTimers.get(settlement.key);
    if (heartbeat) clearInterval(heartbeat);
    this.terminalIntentHeartbeatTimers.delete(settlement.key);
    const intent = this.terminalSettlementIntents.get(settlement.key);
    this.terminalSettlementIntents.delete(settlement.key);
    intent?.releaseTerminalLock?.();
    this.settlementSignal.publishTerminalIntent(
      settlement.recordingId,
      settlement.key,
      false,
    );
  }

  private async retryTerminalMetadataForOwner(ownerId: string): Promise<void> {
    const settlements = [...this.terminalSettlementIntents.values()]
      .map((intent) => intent.settlement)
      .filter((settlement): settlement is PendingRecordingSegmentSettlement =>
        settlement?.ownerId === ownerId);
    for (const settlement of settlements) {
      if (this.settlementTerminalRetryTimers.has(settlement.key)) continue;
      await this.persistTerminalSettlement(settlement);
    }
  }

  private async persistTerminalSettlement(
    settlement: PendingRecordingSegmentSettlement,
    scheduleRetry = true,
  ): Promise<boolean> {
    try {
      const persisted = await this.dependencies.settlements
        .putSegmentSettlementIfState(settlement, 'pending');
      if (!persisted) {
        const current = (await this.dependencies.settlements
          .listSegmentSettlementsForRecording(settlement.recordingId))
          .find((candidate) => candidate.key === settlement.key);
        if (current?.state !== 'terminal') {
          this.clearTerminalIntent(settlement);
          this.diagnostic('segment_settlement_terminal_state_conflict');
          return false;
        }
      } else {
        this.markSettlementMetadataChanged(settlement.recordingId);
      }
      const timer = this.settlementTerminalRetryTimers.get(settlement.key);
      if (timer) clearTimeout(timer);
      this.settlementTerminalRetryTimers.delete(settlement.key);
      this.clearTerminalIntent(settlement);
      await this.refreshPendingSettlementCount().catch(() => {});
      return true;
    } catch {
      if (scheduleRetry) this.scheduleTerminalSettlementPersistence(settlement);
      return false;
    }
  }

  private scheduleTerminalSettlementPersistence(
    settlement: PendingRecordingSegmentSettlement,
    attempt = 1,
  ): void {
    if (attempt > MAX_BACKGROUND_SETTLEMENT_ATTEMPTS) {
      this.diagnostic('segment_settlement_terminal_state_exhausted');
      this.notify(
        'The ticket boundary is safe, but its local review state still needs a retry.',
        'error',
        settlement.recordingId,
      );
      return;
    }
    if (this.settlementTerminalRetryTimers.has(settlement.key)) return;
    const base = this.dependencies.settlementRetryBaseMs ?? DEFAULT_SETTLEMENT_RETRY_BASE_MS;
    const maximum = this.dependencies.settlementRetryMaxMs ?? DEFAULT_SETTLEMENT_RETRY_MAX_MS;
    const delay = Math.min(maximum, base * (2 ** Math.max(0, attempt - 1)));
    const timer = setTimeout(() => {
      this.settlementTerminalRetryTimers.delete(settlement.key);
      void this.persistTerminalSettlement(settlement, false).then((persisted) => {
        if (persisted) {
          this.diagnostic('segment_settlement_terminal_state_recovered');
          return;
        }
        if (this.terminalSettlementIntents.has(settlement.key)) {
          this.scheduleTerminalSettlementPersistence(settlement, attempt + 1);
        }
      });
    }, delay);
    this.settlementTerminalRetryTimers.set(settlement.key, timer);
  }

  private async retrySettlement(settlement: PendingRecordingSegmentSettlement): Promise<void> {
    if (this.terminalSettlementIntents.has(settlement.key)) return;
    const current = (await this.dependencies.settlements
      .listSegmentSettlementsForRecording(settlement.recordingId).catch(() => []))
      .find((candidate) => candidate.key === settlement.key && candidate.state === 'pending');
    if (!current) return;
    const retry = {
      ...current,
      attempts: Math.min(Number.MAX_SAFE_INTEGER, current.attempts + 1),
      lastAttemptAt: this.now().toISOString(),
    };
    try {
      const claimed = await this.dependencies.settlements
        .putSegmentSettlementIfState(retry, 'pending');
      if (!claimed) return;
      await this.sendDurableSettlement(retry);
    } catch {
      this.diagnostic('segment_settlement_recovery_failed');
    }
  }

  private async refreshPendingSettlementCount(): Promise<void> {
    const recordingId = this.snapshot.recordingId;
    if (!recordingId) return;
    const settlements = await this.dependencies.settlements
      .listSegmentSettlementsForRecording(recordingId);
    if (this.snapshot.recordingId === recordingId) {
      this.publish({
        pendingSettlements: settlements.length,
        terminalSettlements: settlements.filter((row) => row.state === 'terminal').length,
        cleanupSettlements: settlements.filter((row) => row.state === 'cleanup').length,
      });
    }
  }

  private applySettlementOutcome(
    settlement: PendingRecordingSegmentSettlement,
    outcome: SettlementOutcome,
  ): void {
    if (outcome.status !== 'settled' || this.snapshot.recordingId !== settlement.recordingId) return;
    if (settlement.action === 'cancel') {
      this.terminalSegmentKeys.add(settlement.key);
      this.recordedMomentKeys.forEach((key) => {
        if (key.startsWith(`${settlement.segmentId}:`)) this.recordedMomentKeys.delete(key);
      });
      this.publish({
        segments: this.snapshot.segments.filter((segment) => segment.id !== settlement.segmentId),
        openSegment: this.snapshot.openSegment?.id === settlement.segmentId
          ? null
          : this.snapshot.openSegment,
      });
      return;
    }
    if (!outcome.segment) return;
    const local = this.snapshot.segments.find((segment) => segment.id === settlement.segmentId);
    this.replaceSegment({
      ...segmentView(outcome.segment),
      snapshotCount: local?.snapshotCount ?? outcome.segment.snapshots?.length ?? 0,
    });
  }

  private errorStatus(error: unknown): number | undefined {
    return typeof error === 'object' && error !== null && 'status' in error
      ? Number((error as { status?: unknown }).status)
      : undefined;
  }

  private settlementErrorCode(error: unknown): string {
    if (typeof error === 'object' && error !== null && 'code' in error) {
      const code = (error as { code?: unknown }).code;
      if (typeof code === 'string' && code.length > 0) return code;
    }
    return `http_${this.errorStatus(error) ?? 'unknown'}`;
  }

  private isTerminalSettlementError(error: unknown): boolean {
    const status = this.errorStatus(error);
    return status === 400 || status === 403 || status === 409;
  }

  private now(): Date {
    return this.dependencies.now?.() ?? new Date();
  }

  private persistStopBoundary = async (boundaryMs: number): Promise<void> => {
    if (this.mutation || this.hydration) {
      let timer: ReturnType<typeof setTimeout> | null = null;
      await Promise.race([
        Promise.allSettled([this.mutation, this.hydration].filter(Boolean)),
        new Promise<void>((resolve) => {
          timer = setTimeout(
            resolve,
            this.dependencies.stopBoundaryStateTimeoutMs ?? STOP_BOUNDARY_STATE_TIMEOUT_MS,
          );
        }),
      ]);
      if (timer) clearTimeout(timer);
    }
    const open = this.snapshot.openSegment;
    if (!open && (this.mutation || this.hydration)) {
      throw new Error('Ticket boundary state did not settle before recorder shutdown.');
    }
    if (!open) return;
    const existing = (await this.dependencies.settlements
      .listSegmentSettlementsForRecording(open.sessionId))
      .find((candidate) => candidate.key === `${open.sessionId}:${open.id}`);
    if (
      existing?.state === 'terminal'
      || this.terminalSettlementIntents.has(`${open.sessionId}:${open.id}`)
    ) {
      this.notify('The saved ticket boundary needs review before it can sync.', 'error', open.sessionId);
      return;
    }
    if (existing?.state === 'cleanup') {
      this.notify('The ticket is saved. Local cleanup is still pending.', 'warning', open.sessionId);
      return;
    }
    const action = existing?.action
      ?? (this.remainingDuration(open, boundaryMs) === 0 ? 'close' : 'cancel');
    const settlement = await this.ensureSettlement(open, action, existing?.endMs ?? boundaryMs);
    void this.sendDurableSettlement(settlement).then((outcome) => {
      if (outcome.status !== 'settled') {
        this.notify(
          outcome.status === 'terminal'
            ? 'The saved ticket boundary needs review before it can sync.'
            : 'The ticket boundary is saved locally for retry.',
          outcome.status === 'terminal' ? 'error' : 'warning',
          open.sessionId,
        );
        return;
      }
      if (action === 'cancel') {
        this.notify(
          'A very short open ticket was removed when recording ended.',
          'warning',
          open.sessionId,
        );
      }
    });
  };
}
